import { computed, effect, inject, untracked } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { Router } from '@angular/router';
import { capabilitySuggestionEvents } from '@indutrust/capability-data';
import type { FactoryForm } from '@indutrust/factory-model';
import {
  computeFactoryProfileCompletion,
  FACTORY_SECTION_ICONS,
  type FactorySection,
  type FactorySectionDeps,
  type FactorySectionNavItem,
  getFactorySectionStatus,
  listFactorySections,
  scrollToFactoryEditSection,
  setupFactoryEditSectionScrollSpy,
} from '@indutrust/factory-util';
import { NotificationStore } from '@indutrust/shared-data';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { injectDispatch } from '@ngrx/signals/events';
import { CompanyFactoriesStore } from '../../company-factory/+state/company-factories.store';
import { FactoryDocumentsStore } from '../../document/+state/factory-documents.store';
import { FactoryApprovalStore } from '../../factory/factory-approval.store';
import { FactoryStore } from '../../factory/factory.store';
import { FactoryImagesStore } from '../../image/+state/factory-images.store';
import { machineParkEvents } from '../../machine-park/+state/machine-park.events';
import { MachineParkStore } from '../../machine-park/+state/machine-park.store';
import { FactoryProjectReferencesStore } from '../../project-reference/+state/factory-project-references.store';
import { virtualTourEvents } from '../../virtual-tour/+state/virtual-tour.events';
import { VirtualTourStore } from '../../virtual-tour/+state/virtual-tour.store';
import { FactoryEditFormStore } from './factory-edit-form.store';

/**
 * Returns true when the given section has active Angular form validation
 * errors that raw-value completeness checks in {@link getFactorySectionStatus}
 * would not catch.
 *
 * The `validity !== 'invalid'` guard in `sectionNavItems` short-circuits
 * before calling this function for sections already marked invalid by the
 * raw-value logic, so we only need to cover the gaps:
 * - `general` / `location`: new `minLength` / `maxLength` violations on
 *   non-empty fields that `resolveGeneral` / `resolveLocation` would pass.
 * - `business`: the cross-field `workshopAreaValidator` and new `min(0)`
 *   violations (business is never `invalid` from raw-value logic alone).
 * - `contacts`: prospect sub-form validity (raw logic only counts `contact_ids`).
 */
function hasSectionFormErrors(
  section: FactorySection,
  entityForm: FieldTree<FactoryForm>
): boolean {
  switch (section) {
    case 'general':
      return entityForm.name().errors().length > 0;
    case 'location':
      return (
        entityForm.address().errors().length > 0 ||
        entityForm.country_id().errors().length > 0 ||
        entityForm.industrial_area_id().errors().length > 0 ||
        entityForm.latitude().errors().length > 0 ||
        entityForm.longitude().errors().length > 0
      );
    case 'business':
      return (
        entityForm.workshop_available().errors().length > 0 ||
        entityForm.workshop_used().errors().length > 0 ||
        entityForm.headcount_management().errors().length > 0 ||
        entityForm.headcount_production().errors().length > 0 ||
        entityForm.turnover().errors().length > 0
      );
    case 'contacts':
      return !entityForm.prospects().valid();
    case 'capabilities':
    case 'documents':
    case 'gallery':
    case 'machinePark':
    case 'projectReferences':
    case 'virtualTour':
      return false;
    default:
      return false;
  }
}

function buildSectionDeps(
  documentsStore: InstanceType<typeof FactoryDocumentsStore>,
  imagesStore: InstanceType<typeof FactoryImagesStore>,
  machineParkStore: InstanceType<typeof MachineParkStore>,
  projectReferencesStore: InstanceType<typeof FactoryProjectReferencesStore>,
  virtualTourStore: InstanceType<typeof VirtualTourStore>
): FactorySectionDeps {
  return {
    documentsCount: documentsStore.entities().length,
    imagesCount: imagesStore.entities().length,
    machineParkCount: machineParkStore.entities().length,
    projectReferencesCount: projectReferencesStore.entities().length,
    virtualTourNodesCount: virtualTourStore.nodes().length,
  };
}

/**
 * Scoped store for factory editor page UX: section navigation, weighted
 * {@link profileCompletion}, toasts, post-delete redirect, and workflow
 * refresh side-effects.
 *
 * @example
 * const uiStore = inject(FactoryEditUiStore);
 * uiStore.scrollToSection('location');
 */
export const FactoryEditUiStore = signalStore(
  withState<{
    activeSection: FactorySection | null;
    isAutoScrolling: boolean;
    scrollSeq: number;
  }>({
    activeSection: 'general',
    isAutoScrolling: false,
    scrollSeq: 0,
  }),
  withComputed(
    (
      store,
      formStore = inject(FactoryEditFormStore),
      documentsStore = inject(FactoryDocumentsStore),
      imagesStore = inject(FactoryImagesStore),
      machineParkStore = inject(MachineParkStore),
      projectReferencesStore = inject(FactoryProjectReferencesStore),
      virtualTourStore = inject(VirtualTourStore)
    ) => ({
      sectionNavItems: computed<FactorySectionNavItem[]>(() => {
        const value = formStore.formModel();
        const deps = buildSectionDeps(
          documentsStore,
          imagesStore,
          machineParkStore,
          projectReferencesStore,
          virtualTourStore
        );
        const sections: readonly FactorySection[] = listFactorySections();
        return sections.map((id) => {
          let validity = getFactorySectionStatus(id, value, deps);
          if (
            validity !== 'invalid' &&
            hasSectionFormErrors(id, formStore.entityForm)
          ) {
            validity = 'invalid';
          }
          return { id, icon: FACTORY_SECTION_ICONS[id], validity };
        });
      }),
      profileCompletion: computed(() =>
        computeFactoryProfileCompletion(
          formStore.formModel(),
          buildSectionDeps(
            documentsStore,
            imagesStore,
            machineParkStore,
            projectReferencesStore,
            virtualTourStore
          )
        )
      ),
    })
  ),
  withMethods((store) => {
    function beginAutoScroll(section: FactorySection): void {
      const seq = store.scrollSeq() + 1;
      patchState(store, {
        activeSection: section,
        isAutoScrolling: true,
        scrollSeq: seq,
      });
      scrollToFactoryEditSection(section)
        .then(() => {
          if (store.scrollSeq() === seq) {
            patchState(store, {
              isAutoScrolling: false,
            });
          }
        })
        .catch(() => undefined);
    }
    return {
      scrollToFirstInvalidSection(): FactorySection | null {
        const first = store
          .sectionNavItems()
          .find((item) => item.validity === 'invalid');
        if (first) {
          beginAutoScroll(first.id);
        }
        return first?.id ?? null;
      },
      scrollToSection(section: FactorySection): void {
        beginAutoScroll(section);
      },
      setActiveSection(section: FactorySection): void {
        patchState(store, { activeSection: section });
      },
    };
  }),
  withHooks((store) => {
    const approvalStore = inject(FactoryApprovalStore);
    const companyFactoriesStore = inject(CompanyFactoriesStore);
    const documentsStore = inject(FactoryDocumentsStore);
    const factoryStore = inject(FactoryStore);
    const imagesStore = inject(FactoryImagesStore);
    const formStore = inject(FactoryEditFormStore);
    const machineParkDispatch = injectDispatch(machineParkEvents);
    const notificationStore = inject(NotificationStore);
    const projectReferencesStore = inject(FactoryProjectReferencesStore);
    const router = inject(Router);
    const suggestionDispatch = injectDispatch(capabilitySuggestionEvents);
    const virtualTourDispatch = injectDispatch(virtualTourEvents);

    const notify = (messageKey: string) => {
      notificationStore.notify({
        config: {
          messageKey,
          scope: 'shared-factory-edit',
          titleKey: `${messageKey}Title`,
          type: 'success',
        },
      });
    };

    return {
      onInit() {
        effect(() => {
          const factoryId = formStore.entityId();
          if (typeof factoryId === 'number' && factoryId > 0) {
            untracked(() => {
              documentsStore.loadAll({ factoryId });
              imagesStore.loadAll({ factoryId });
              machineParkDispatch.loadRequested({ factoryId });
              suggestionDispatch.loadRequested({ factoryId });
              projectReferencesStore.loadAll({ factoryId });
              virtualTourDispatch.loadRequested({ factoryId });
            });
          }
        });

        // Capture status at store init so effects detect transitions, not
        // pre-existing values when navigating back to the edit page.
        let previousEditStatus = factoryStore.editStatus();
        let previousDeleteStatus = factoryStore.deleteStatus();
        let previousApprovalPhase = approvalStore.phase();

        effect(() => {
          const editStatus = factoryStore.editStatus();
          const prev = previousEditStatus;
          previousEditStatus = editStatus;
          if (editStatus !== 'success' || prev === 'success') {
            return;
          }
          untracked(() => {
            imagesStore.commitOrder();
            notify('toast.saveSuccess');
          });
        });

        effect(() => {
          const phase = approvalStore.phase();
          const prev = previousApprovalPhase;
          previousApprovalPhase = phase;
          if (
            phase === prev ||
            (phase !== 'submitted' && phase !== 'published')
          ) {
            return;
          }
          const key =
            phase === 'submitted'
              ? 'submitForApprovalSuccess'
              : 'publishSuccess';
          untracked(() => {
            notify(`toast.${key}`);
            const factoryId = formStore.entityId();
            if (typeof factoryId === 'number' && factoryId > 0) {
              factoryStore.loadById(factoryId);
              companyFactoriesStore.loadAll();
            }
          });
        });

        effect(() => {
          const deleteStatus = factoryStore.deleteStatus();
          const prev = previousDeleteStatus;
          previousDeleteStatus = deleteStatus;
          if (deleteStatus !== 'success' || prev === 'success') {
            return;
          }
          untracked(() => {
            void router.navigate(['company-profile']);
          });
        });

        effect((onCleanup) => {
          if (
            !factoryStore.entity() ||
            typeof IntersectionObserver === 'undefined'
          ) {
            return;
          }
          let disconnect: (() => void) | undefined;
          queueMicrotask(() => {
            disconnect = setupFactoryEditSectionScrollSpy(store);
          });
          onCleanup(() => disconnect?.());
        });
      },
    };
  })
);
